import React, { useState } from 'react'
import styled from '@emotion/styled'

import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Card } from '@amzn/stencil-react-components/card'
import { Checkbox } from '@amzn/stencil-react-components/checkbox'
import { InputWrapper, LabelPosition } from '@amzn/stencil-react-components/input'
import { Col, Row } from '@amzn/stencil-react-components/layout'
import { Spinner } from '@amzn/stencil-react-components/spinner'
import { Status, StatusIndicator } from '@amzn/stencil-react-components/status-indicator'
import { Table, TableColumn } from '@amzn/stencil-react-components/table'
import { H2, Text } from '@amzn/stencil-react-components/text'
import { ToggleButton, ToggleButtonGroup } from '@amzn/stencil-react-components/toggle-button'

import {
    FIRST_DATA_LINE,
    MAX_CONTAINERS_PER_IMPORT,
    mustSkipContainerCreation,
    ParsedSpreadsheet,
    pluralize,
    ROWS_PER_PAGE,
    RowValidation,
    truncateCell,
} from 'src/pages/import-content/ImportContentModel'

export interface ReviewStepProps {
    spreadsheet: ParsedSpreadsheet
    page: number
    onPageChange: (page: number) => void
    canPreview: boolean
    onPreviewRow: (row: Record<string, string>) => void
    isPreviewing: boolean
    canSubmit: boolean
    onSubmit: () => void
    onBack: () => void
    rowValidation?: RowValidation
    isValidatingRows: boolean
    validatedRowCount: number
    itemsPerRow: number
    skipContainerCreation: boolean
    onSkipContainerCreationChange: (skip: boolean) => void
}

type SpreadsheetRow = Record<string, string>

// Solid separator between the validation column and the spreadsheet columns.
// Stencil's table container is inline-flex, so it only fills the width when a flex parent stretches it.
const RowsTableContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    'th:first-of-type, td:first-of-type': {
        borderRight: '1px solid #8B96A3',
    },
})

enum RowFilter {
    All = 'all',
    Errors = 'errors',
}

export const ReviewStep = ({
    spreadsheet,
    page,
    onPageChange,
    canPreview,
    onPreviewRow,
    isPreviewing,
    canSubmit,
    onSubmit,
    onBack,
    rowValidation,
    isValidatingRows,
    validatedRowCount,
    itemsPerRow,
    skipContainerCreation,
    onSkipContainerCreationChange,
}: ReviewStepProps) => {
    const [rowFilter, setRowFilter] = useState(RowFilter.All)
    const isSkipContainerCreationForced = mustSkipContainerCreation(spreadsheet)

    const errorsByRow = rowValidation?.byRow ?? {}
    const needsAttentionCount = Object.keys(errorsByRow).length
    const readyCount = spreadsheet.rowCount - needsAttentionCount

    const allRowIndices = spreadsheet.rows.map((_row, rowIndex) => rowIndex)
    const visibleRowIndices =
        rowFilter === RowFilter.Errors
            ? allRowIndices.filter((rowIndex) => errorsByRow[rowIndex])
            : allRowIndices
    const pageStart = page * ROWS_PER_PAGE
    const pageCount = Math.ceil(visibleRowIndices.length / ROWS_PER_PAGE)
    const pageRowIndices = visibleRowIndices.slice(pageStart, pageStart + ROWS_PER_PAGE)
    const pageRows = pageRowIndices.map((rowIndex) => spreadsheet.rows[rowIndex])

    const onFilterChange = (filter: RowFilter) => {
        setRowFilter(filter)
        onPageChange(0)
    }

    const columns: TableColumn<SpreadsheetRow>[] = [
        {
            header: 'CSV Row',
            width: '300px',
            cellComponent: ({ index }: { index: number }) => {
                const rowIndex = pageRowIndices[index]
                const messages = errorsByRow[rowIndex]
                return (
                    <Col gridGap='S100' dataTestId={`row-status-${rowIndex}`}>
                        <Row alignItems='center' gridGap='S200'>
                            <Text fontSize='T100' fontWeight='bold' color='neutral90'>
                                Row {rowIndex + FIRST_DATA_LINE}
                            </Text>
                            {isValidatingRows ? (
                                <StatusIndicator status={Status.Loading} messageText='Checking…' />
                            ) : (
                                <StatusIndicator
                                    status={messages ? Status.Negative : Status.Positive}
                                    messageText={messages ? 'Needs attention' : 'Ready'}
                                />
                            )}
                        </Row>
                        {messages?.map((message) => (
                            <Text key={message} fontSize='T100' color='red70'>
                                {message}
                            </Text>
                        ))}
                    </Col>
                )
            },
        },
        ...spreadsheet.headers.map((header) => ({
            header,
            cellComponent: ({ data }: { data: SpreadsheetRow }) => (
                <Text fontSize='T100' color='neutral90'>
                    {truncateCell(data[header])}
                </Text>
            ),
        })),
        {
            header: 'Preview',
            width: '96px',
            cellComponent: ({ data, index }: { data: SpreadsheetRow; index: number }) => (
                <Button
                    dataTestId={`preview-row-${pageRowIndices[index]}`}
                    size={ButtonSize.Small}
                    variant={ButtonVariant.Secondary}
                    aria-disabled={!canPreview || !!errorsByRow[pageRowIndices[index]]}
                    onClick={() => onPreviewRow(data)}
                >
                    Preview
                </Button>
            ),
        },
    ]

    return (
        <Card isElevated width='100%' dataTestId='import-content-review-step'>
            <Col gridGap='S300' width='100%'>
                <Row justifyContent='space-between' alignItems='center' gridGap='S300' width='100%'>
                    <H2 fontSize='T300'>Review your spreadsheet template</H2>
                    <Row alignItems='center' gridGap='S300'>
                        {isPreviewing && <Spinner dataTestId='import-content-spinner' />}
                        <Button
                            dataTestId='import-content-create'
                            variant={ButtonVariant.Primary}
                            aria-disabled={!canSubmit}
                            onClick={onSubmit}
                        >
                            Create
                        </Button>
                    </Row>
                </Row>
                <Text fontSize='T200' color='neutral70' dataTestId='review-file-totals'>
                    {skipContainerCreation ? (
                        <>
                            This file adds {pluralize(spreadsheet.rowCount * itemsPerRow, 'item')}{' '}
                            straight to the item pool
                        </>
                    ) : (
                        <>
                            This file contains{' '}
                            {pluralize(spreadsheet.containerCount, 'Item Container module')} and{' '}
                            {pluralize(spreadsheet.rowCount * itemsPerRow, 'item')}
                        </>
                    )}
                </Text>

                <Col gridGap='S100'>
                    <InputWrapper
                        id='skip-container-creation'
                        labelText='Skip Item Container creation'
                        labelPosition={LabelPosition.Trailing}
                    >
                        {(inputProps) => (
                            <Checkbox
                                {...inputProps}
                                dataTestId='skip-container-creation'
                                checked={skipContainerCreation}
                                disabled={isSkipContainerCreationForced}
                                onChange={() =>
                                    onSkipContainerCreationChange(!skipContainerCreation)
                                }
                            />
                        )}
                    </InputWrapper>
                    {isSkipContainerCreationForced && (
                        <Text
                            fontSize='T100'
                            color='neutral70'
                            dataTestId='skip-container-creation-reason'
                        >
                            {spreadsheet.hasContainerColumn
                                ? `This file has ${spreadsheet.containerCount} containers, but at most ${MAX_CONTAINERS_PER_IMPORT} Item Container modules are created per import.`
                                : 'This file has no __container__ column.'}
                        </Text>
                    )}
                </Col>

                <Row alignItems='center' gridGap='S300' dataTestId='review-summary'>
                    <Text fontSize='T200' fontWeight='bold'>
                        {pluralize(spreadsheet.rowCount, 'row')} to import
                    </Text>
                    {isValidatingRows ? (
                        <StatusIndicator
                            status={Status.Loading}
                            messageText={`Validating rows — ${validatedRowCount} of ${spreadsheet.rowCount} checked`}
                        />
                    ) : (
                        <>
                            <StatusIndicator
                                status={Status.Positive}
                                messageText={`${readyCount} ready`}
                            />
                            {needsAttentionCount > 0 && (
                                <StatusIndicator
                                    status={Status.Negative}
                                    messageText={`${needsAttentionCount} need${
                                        needsAttentionCount === 1 ? 's' : ''
                                    } attention`}
                                />
                            )}
                        </>
                    )}
                </Row>

                <Row justifyContent='space-between' alignItems='center' gridGap='S300' width='100%'>
                    <ToggleButtonGroup
                        value={rowFilter}
                        onChange={(_event, value) => onFilterChange(value as RowFilter)}
                    >
                        <ToggleButton value={RowFilter.All} dataTestId='filter-all-rows'>
                            All rows ({spreadsheet.rowCount})
                        </ToggleButton>
                        <ToggleButton value={RowFilter.Errors} dataTestId='filter-error-rows'>
                            Error only ({needsAttentionCount})
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Text fontSize='T200' dataTestId='csv-rows-showing'>
                        Showing {pageRows.length} of {visibleRowIndices.length}
                    </Text>
                </Row>

                <RowsTableContainer data-test-id='csv-rows-container'>
                    <Table
                        dataTestId='csv-rows'
                        aria-label='Spreadsheet rows to import'
                        columns={columns}
                        data={pageRows}
                        rowKeyAccessor={(_row: SpreadsheetRow, index: number) =>
                            String(pageRowIndices[index])
                        }
                        hasInteractiveElement
                        shouldScrollHorizontally
                    />
                </RowsTableContainer>

                {visibleRowIndices.length > ROWS_PER_PAGE && (
                    <Row justifyContent='center' alignItems='center' gridGap='S300'>
                        <Button
                            dataTestId='csv-rows-prev'
                            size={ButtonSize.Small}
                            variant={ButtonVariant.Tertiary}
                            aria-disabled={page === 0}
                            onClick={() => onPageChange(page - 1)}
                        >
                            Previous
                        </Button>
                        <Text fontSize='T100' color='neutral70'>
                            Page {page + 1} of {pageCount}
                        </Text>
                        <Button
                            dataTestId='csv-rows-next'
                            size={ButtonSize.Small}
                            variant={ButtonVariant.Tertiary}
                            aria-disabled={page >= pageCount - 1}
                            onClick={() => onPageChange(page + 1)}
                        >
                            Next
                        </Button>
                    </Row>
                )}

                <Row>
                    <Button
                        dataTestId='import-content-back-to-upload'
                        variant={ButtonVariant.Tertiary}
                        onClick={onBack}
                    >
                        Back
                    </Button>
                </Row>
            </Col>
        </Card>
    )
}
